import React, {useEffect, useState} from "react"
import {Trans, Plural} from "@lingui/react/macro"
import styles from "./styles.module.css"
import ApiClient from "./apiClient"
import CategoryCard from "./CategoryCard"
import ArticleCard from "./ArticleCard"
import SearchBar from "./SearchBar"
import Pagination from "./Pagination"

const PER_PAGE = 12

export default function Home({context, langValue}) {
    const [categories, setCategories] = useState([])
    const [articles, setArticles] = useState([])
    const [page, setPage] = useState(1)
    const [total, setTotal] = useState(0)
    const [loadingCats, setLoadingCats] = useState(true)
    const [loadingArticles, setLoadingArticles] = useState(true)
    const [search, setSearch] = useState("")
    const [searchDebounced, setSearchDebounced] = useState("")

    useEffect(() => {
        let cancelled = false
        setLoadingCats(true)
        ApiClient.getCategories(langValue).then(data => {
            if (cancelled) return
            setCategories(data?.items || [])
            setLoadingCats(false)
        }).catch(() => !cancelled && setLoadingCats(false))
        return () => { cancelled = true }
    }, [langValue])

    useEffect(() => {
        const timer = setTimeout(() => setSearchDebounced(search.trim()), 300)
        return () => clearTimeout(timer)
    }, [search])

    useEffect(() => {
        let cancelled = false
        setLoadingArticles(true)
        ApiClient.listArticles({
            langValue, page, perPage: PER_PAGE,
            q: searchDebounced || null,
        }).then(data => {
            if (cancelled) return
            setArticles(data?.items || [])
            setTotal(data?.total || 0)
            setLoadingArticles(false)
        }).catch(() => !cancelled && setLoadingArticles(false))
        return () => { cancelled = true }
    }, [langValue, page, searchDebounced])

    useEffect(() => { setPage(1) }, [searchDebounced])

    const totalPages = Math.max(1, Math.ceil(total / PER_PAGE))
    const topCategories = (categories || []).slice(0, 8)

    return (
        <>
            <section className={styles.hero}>
                <div className={styles.heroEyebrow}>{context.org?.name || ""}</div>
                <h1 className={styles.heroTitle}><Trans>Explorer la bibliothèque</Trans></h1>
                <p className={styles.heroSubtitle}>
                    <Trans>Parcourez articles et catégories de contenu juif.</Trans>
                </p>
                <SearchBar value={search} onChange={setSearch}/>
            </section>

            {!searchDebounced && topCategories.length > 0 && (
                <section className={styles.section}>
                    <div className={styles.sectionHeader}>
                        <h2 className={styles.sectionTitle}><Trans>Catégories</Trans></h2>
                    </div>
                    {loadingCats ? (
                        <div className={styles.loading}><div className={styles.loadingSpinner}/></div>
                    ) : (
                        <div className={styles.categoriesGrid}>
                            {topCategories.map(cat => (
                                <CategoryCard key={cat.aid} context={context} category={cat}/>
                            ))}
                        </div>
                    )}
                </section>
            )}

            <section className={styles.section}>
                <div className={styles.sectionHeader}>
                    <h2 className={styles.sectionTitle}>
                        {searchDebounced ? `« ${searchDebounced} »` : <Trans>Articles récents</Trans>}
                    </h2>
                    {!searchDebounced && total > 0 && (
                        <span className={styles.sectionLink}>
                            <Plural value={total} one="# article" other="# articles"/>
                        </span>
                    )}
                </div>

                {loadingArticles ? (
                    <div className={styles.articlesGrid}>
                        {Array.from({length: 8}).map((_, i) => (
                            <div key={i} className={`${styles.skeleton} ${styles.skeletonCard}`}/>
                        ))}
                    </div>
                ) : articles.length === 0 ? (
                    <div className={styles.empty}>
                        <div className={styles.emptyIcon}>📭</div>
                        <p><Trans>Aucun article trouvé.</Trans></p>
                    </div>
                ) : (
                    <>
                        <div className={styles.articlesGrid}>
                            {articles.map(a => (
                                <ArticleCard key={a.aid} context={context} article={a}/>
                            ))}
                        </div>
                        <Pagination
                            page={page}
                            totalPages={totalPages}
                            onChange={(p) => { setPage(p); window.scrollTo({top: 0, behavior: "smooth"}) }}
                        />
                    </>
                )}
            </section>
        </>
    )
}
